// drawOla multi-file storage: searchable text of a scene.
//
// Fork-only. No upstream counterpart, so it never conflicts on merge.

import type { OrderedExcalidrawElement } from "@excalidraw/element/types";

/**
 * Collect the strings a scene can be searched by.
 *
 * Two sources: text elements (standalone text AND shape-bound labels are both
 * `type: "text"`, so one branch covers them) and frame names. Deleted elements
 * are left out, and a malformed element is skipped rather than throwing: this
 * runs over every stored doc on the Studio's list path, and one bad element
 * from an old import must not cost the whole list.
 *
 * @param elements the doc's stored elements
 * @returns trimmed, non-empty strings in element order
 */
export function extractSearchStrings(
  elements: readonly OrderedExcalidrawElement[],
): string[] {
  const strings: string[] = [];

  for (const element of elements) {
    if (!element || element.isDeleted) {
      continue;
    }

    if (element.type === "text") {
      // `originalText` is the source the user typed. The rendered `text` has
      // newlines inserted by word-wrap, which would break a substring match
      // across a wrap point, so prefer the unwrapped one.
      push(strings, element.originalText || element.text);
      continue;
    }

    if (element.type === "frame" || element.type === "magicframe") {
      push(strings, element.name);
    }
  }

  return strings;
}

/** Append `value` when it is a string with something left after trimming. */
function push(strings: string[], value: unknown): void {
  if (typeof value !== "string") {
    return;
  }
  const trimmed = value.trim();
  if (trimmed) {
    strings.push(trimmed);
  }
}
